import { getFirestore } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app, storage } from "../../../firebase-config.js";
import { PlazaService } from "../../../services/plaza.service.js";
import { locale as LocaleService } from "../../../services/LocaleService.js";

const db = getFirestore(app);
const auth = getAuth(app);

export class PostComposer {
    constructor(container, onSuccess) {
        this.container = container;
        this.onSuccess = onSuccess;
        // Blocks: Array of { id, text, files: [], mentionedUsers: Map }
        this.blocks = [];
        this.blockCounter = 0;
        this.quoteFeed = null; // [NEW] Quote Data

        // Add initial block
        this.addBlockData();
    }

    // [NEW] Activate Quote Mode
    activateQuoteMode(feedData) {
        this.quoteFeed = feedData;
        this.blocks = []; // Reset blocks
        this.blockCounter = 0;
        this.addBlockData();
        this.render(); // Re-render to show quote UI

        // Focus input
        // Focus input (Delayed to prevent aria-hidden warning from SweetAlert2 closing)
        setTimeout(() => {
            const firstInput = this.blocksContainer.querySelector('textarea');
            if (firstInput) {
                firstInput.focus();
                // Ensure we see the start of the composer, not just the input
                this.resultWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 500);
    }

    cancelQuoteMode() {
        this.quoteFeed = null;
        this.render();
    }

    addBlockData() {
        const id = `block-${this.blockCounter++}`;
        this.blocks.push({
            id,
            text: '',
            files: [], // { file, previewUrl }
            mentionedUsers: new Map()
        });
        return id;
    }

    removeBlockData(id) {
        if (this.blocks.length <= 1) return;
        this.blocks = this.blocks.filter(b => b.id !== id);
    }

    escapeHtml(text) {
        if (!text) return '';
        return text
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
    }

    render() {
        // Wrapper for the whole composer
        const wrapper = document.createElement('div');
        wrapper.className = "mb-8 animate-fade-in relative z-20";

        // Quote Preview HTML
        let quotePreviewHTML = '';
        if (this.quoteFeed) {
            const q = this.quoteFeed;
            const qImg = (q.imageUrls && q.imageUrls[0]) || q.imageUrl || null;
            quotePreviewHTML = `
                <div class="mx-4 mt-4 mb-2 p-3 bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-700 rounded-xl flex gap-3 relative group/quote">
                    ${qImg ? `<div class="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden shrink-0"><img src="${qImg}" class="w-full h-full object-cover"></div>` : ''}
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-1 mb-0.5">
                            <span class="text-xs font-bold text-gray-900 dark:text-gray-100">${this.escapeHtml(q.authorName)}</span>
                            <span class="text-[10px] text-gray-400">${LocaleService.t('plaza.quote_suffix')}</span>
                        </div>
                        <p class="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">${this.escapeHtml(q.content)}</p>
                    </div>
                    <button id="btn-cancel-quote" class="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors">
                        <span class="material-symbols-outlined text-sm">close</span>
                    </button>
                </div>
            `;
        }

        // We will re-render the list of blocks inside 'blocks-container'
        wrapper.innerHTML = `
            <div class="relative group">
                <div class="absolute -inset-1 bg-gradient-to-r ${this.quoteFeed ? 'from-green-500 to-teal-500' : 'from-violet-600 to-indigo-600'} rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                <div class="relative bg-white dark:bg-zinc-900 ring-1 ring-gray-900/5 rounded-2xl p-4">
                    
                    ${quotePreviewHTML}

                    <div id="blocks-container" class="flex flex-col gap-0 relative">
                        <!-- Blocks injected here -->
                    </div>

                    <!-- Footer Actions -->
                    <div class="flex items-center justify-between mt-4 pt-2 border-t border-gray-100 dark:border-zinc-800">
                        <div class="flex items-center gap-2">
                             <button id="btn-add-thread" class="text-violet-500 hover:bg-violet-50 dark:hover:bg-zinc-800 px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1 transition-colors">
                                <span class="material-symbols-outlined text-lg">add_circle</span>
                                <span>${LocaleService.t('plaza.btn_add_thread')}</span>
                            </button>
                        </div>
                        <button id="btn-post-submit" class="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded-xl font-bold transition-all shadow-lg hover:shadow-violet-500/30 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                            <span class="material-symbols-outlined">send</span>
                            <span>${this.quoteFeed ? LocaleService.t('plaza.btn_submit_quote') : LocaleService.t('plaza.btn_submit')}</span>
                        </button>
                    </div>
                </div>
            </div>
        `;

        this.container.innerHTML = '';
        this.container.appendChild(wrapper);

        this.resultWrapper = wrapper;
        this.blocksContainer = wrapper.querySelector('#blocks-container');
        this.btnSubmit = wrapper.querySelector('#btn-post-submit');
        this.btnAdd = wrapper.querySelector('#btn-add-thread');

        // Bind Cancel Quote
        const btnCancelQuote = wrapper.querySelector('#btn-cancel-quote');
        if (btnCancelQuote) {
            btnCancelQuote.onclick = () => this.cancelQuoteMode();
        }

        this.renderBlocks(); // Initial Render
        this.bindGlobalEvents();
        this.startAuthListener();

        // [FIX] Portal Dropdown: Move dropdown to body to avoid Z-Index/Overflow issues
        this.portalDropdownId = 'global-mention-dropdown-portal';
        let existingDropdown = document.getElementById(this.portalDropdownId);
        if (!existingDropdown) {
            existingDropdown = document.createElement('div');
            existingDropdown.id = this.portalDropdownId;
            // [FIX] Coordinate System: Use 'absolute' to work with scrollY correctly
            existingDropdown.className = "hidden absolute w-64 bg-white dark:bg-zinc-800 rounded-xl shadow-2xl border border-gray-100 dark:border-zinc-700 overflow-hidden z-[9999] max-h-60 overflow-y-auto";
            document.body.appendChild(existingDropdown);
        }
        this.dropdown = existingDropdown;
    }

    renderBlocks() {
        this.blocksContainer.innerHTML = '';

        this.blocks.forEach((block, index) => {
            const isLast = index === this.blocks.length - 1;
            const blockEl = document.createElement('div');
            // Visual Connector Line
            const connectorClass = !isLast ?
                "before:absolute before:left-[19px] before:top-12 before:bottom-[-16px] before:w-[2px] before:bg-gray-200 dark:before:bg-zinc-700 before:z-0" : "";

            blockEl.className = `flex items-start gap-4 relative z-10 ${index > 0 ? 'mt-4' : ''} ${connectorClass}`;
            blockEl.innerHTML = `
            <div class="shrink-0 relative z-10 block-avatar-container" data-idx="${index}">
                ${this.getAvatarHTML()}
            </div>
            <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-bold text-gray-400">Part ${index + 1}</span>
                    ${this.blocks.length > 1 ? `
                            <button class="btn-remove-block text-gray-300 hover:text-red-500 transition-colors" data-id="${block.id}" title="삭제">
                                <span class="material-symbols-outlined text-sm">close</span>
                            </button>
                        ` : ''}
                </div>
                <textarea id="input-${block.id}" rows="${index === 0 ? 3 : 2}"
                    class="w-full bg-transparent border-0 focus:ring-0 text-gray-900 dark:text-white placeholder:text-gray-400 resize-none text-lg p-0 text-left leading-relaxed"
                    placeholder="${index === 0 ? (this.quoteFeed ? LocaleService.t('plaza.composer_placeholder_quote') : LocaleService.t('plaza.composer_placeholder_init')) : LocaleService.t('plaza.composer_placeholder_thread')}">${block.text}</textarea>

                <!-- Image Preview -->
                <div id="preview-${block.id}" class="${block.files.length > 0 ? 'grid' : 'hidden'} grid-cols-3 sm:grid-cols-4 gap-2 mt-2 mb-2">
                    ${block.files.map((fileItem, fIdx) => `
                            <div class="relative aspect-square rounded-lg overflow-hidden group/item">
                                <img src="${fileItem.previewUrl}" class="w-full h-full object-cover">
                                <button class="btn-remove-file absolute top-1 right-1 bg-black/50 hover:bg-red-500 text-white p-0.5 rounded-full transition-colors opacity-0 group-hover/item:opacity-100" data-block="${block.id}" data-idx="${fIdx}">
                                    <span class="material-symbols-outlined text-sm">close</span>
                                </button>
                            </div>
                        `).join('')}
                </div>

                <!-- Block Toolbar -->
                <div class="flex items-center gap-2 text-gray-400">
                    ${index === 0 ? `
                        <input type="file" id="file-${block.id}" accept="image/*" multiple class="hidden">
                        <button class="btn-img-trigger p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors relative" data-id="${block.id}" title="이미지 첨부">
                            <span class="material-symbols-outlined text-xl">image</span>
                            ${block.files.length > 0 ? `<span class="absolute -top-1 -right-1 bg-violet-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">${block.files.length}</span>` : ''}
                        </button>
                    ` : ''}
                </div>
            </div>
        `;
            this.blocksContainer.appendChild(blockEl);

            // Bind Block Specific Events
            this.bindBlockEvents(blockEl, block);
        });
    }

    startAuthListener() {
        onAuthStateChanged(auth, (user) => {
            const avatars = this.blocksContainer.querySelectorAll('.block-avatar-container');
            avatars.forEach(el => el.innerHTML = this.getAvatarHTML());
        });
    }

    getAvatarHTML() {
        const user = getAuth(app).currentUser || window.user;
        if (!user) return `<div class="w-10 h-10 rounded-full bg-zinc-200 dark:bg-zinc-800 animate-pulse"></div>`;
        return user.photoURL
            ? `<img src="${user.photoURL}" class="w-10 h-10 rounded-full object-cover border border-violet-100 dark:border-violet-900 shadow-sm bg-white">`
            : `<div class="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center text-violet-600 font-bold text-sm">${this.escapeHtml(user.displayName)?.[0] || 'U'}</div>`;
    }

    bindGlobalEvents() {
        this.btnAdd.onclick = () => {
            this.addBlockData();
            this.renderBlocks();
            // Focus new block
            const lastId = this.blocks[this.blocks.length - 1].id;
            setTimeout(() => {
                const input = this.blocksContainer.querySelector(`#input-${lastId}`);
                if (input) input.focus();
            }, 50);
        };

        this.btnSubmit.onclick = () => this.handleSubmit();

        // Close dropdown
        document.addEventListener('click', (e) => {
            if (!this.dropdown.contains(e.target) && !e.target.tagName.match(/TEXTAREA/)) {
                this.hideDropdown();
            }
        });
    }

    bindBlockEvents(el, block) {
        const input = el.querySelector(`#input-${block.id}`);
        // Sync text
        input.oninput = (e) => {
            block.text = e.target.value;
            // Auto resize
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';

            this.handleMentions(e, block);
        };

        // Remove Block
        const btnRemove = el.querySelector('.btn-remove-block');
        if (btnRemove) {
            btnRemove.onclick = () => {
                this.removeBlockData(block.id);
                this.renderBlocks();
            };
        }

        // Image Upload (Only for first block)
        const fileInput = el.querySelector(`#file-${block.id}`);
        const btnImg = el.querySelector(`.btn-img-trigger`);

        if (fileInput && btnImg) {
            btnImg.onclick = () => fileInput.click();

            fileInput.onchange = async (e) => {
                const files = Array.from(e.target.files);
                if (files.length === 0) return;

                if (block.files.length + files.length > 5) {
                    alert("한 스레드당 사진은 최대 5장입니다.");
                    fileInput.value = '';
                    return;
                }

                for (const file of files) {
                    if (!file.type.startsWith('image/')) continue;
                    const previewUrl = URL.createObjectURL(file);
                    block.files.push({ file, previewUrl });
                }
                this.renderBlocks(); // Re-render to show previews
            };
        }

        // Remove File
        el.querySelectorAll('.btn-remove-file').forEach(btn => {
            btn.onclick = (e) => {
                const idx = parseInt(e.currentTarget.dataset.idx);
                block.files.splice(idx, 1);
                this.renderBlocks();
            };
        });
    }

    // --- Mention Logic Reuse (Adapted) ---
    async handleMentions(e, block) {
        const input = e.target;
        const value = input.value;
        const cursor = input.selectionStart;

        // [FIX] Support spaces in nicknames using lastIndexOf
        // Search backwards from cursor for the last '@'
        const textUpToCursor = value.substring(0, cursor);
        const lastAt = textUpToCursor.lastIndexOf('@');

        // Reset if no '@' or if the '@' is not at the start or preceded by a space
        // (i.e., we don't want to trigger on "email@example.com")
        if (lastAt === -1) {
            this.hideDropdown();
            return;
        }

        const charBeforeAt = lastAt > 0 ? textUpToCursor[lastAt - 1] : '';
        if (charBeforeAt && !/\s/.test(charBeforeAt)) {
            // precedent char exists and is NOT a space -> likely an email or mid-word
            this.hideDropdown();
            return;
        }

        // Extract the potential name: from '@' to cursor
        const searchTerm = textUpToCursor.substring(lastAt + 1);

        // Optional: Limit search term length to prevent searching huge strings
        if (searchTerm.length > 20) {
            this.hideDropdown();
            return;
        }

        // [FIX] Race Condition: Abort previous request
        if (this.searchAbortController) {
            this.searchAbortController.abort();
        }
        this.searchAbortController = new AbortController();
        const signal = this.searchAbortController.signal;

        // [FIX] Debounce is tricky with async/await in this structure, 
        // effectively handled by AbortController + short timeout if needed.
        // Let's rely on AbortController for race condition stability.

        try {
            const users = await PlazaService.searchUsers(searchTerm); // Service should ideally accept 'signal'

            if (signal.aborted) return; // Ignore if new request came in

            if (users.length > 0) {
                // Determine currentWord for replacement logic (Raw string from @ to cursor)
                this.showDropdown(users, searchTerm, block, input, lastAt);
            } else {
                this.hideDropdown();
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Mention search error:", error);
            }
        }
    }

    showDropdown(users, searchTerm, block, inputEl, atIndex) {
        this.dropdown.innerHTML = '';
        this.dropdown.classList.remove('hidden');

        // Repositioning Logic
        // We want the dropdown near the cursor, or roughly near the input line if simple.
        // For accurate cursor positioning, a mirror div is needed (Textarea Caret Position).
        // For MVP stability: Position at Input's bottom-left, but offset slightly if possible?
        // Let's stick to Input Box Bottom for robustness (avoid off-screen complexity).

        const rect = inputEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;

        // [FIX] Coordinate System: 
        // We use position: absolute on body.
        // Top = rect.bottom (viewport relative) + window.scrollY (scroll offset)

        let topPos;
        if (spaceBelow < 250) {
            // Show above: (rect.top + scrollY) - dropdownHeight
            topPos = (rect.top + window.scrollY - this.dropdown.offsetHeight - 5);
        } else {
            // Show below: (rect.bottom + scrollY)
            topPos = (rect.bottom + window.scrollY + 5);
        }

        this.dropdown.style.top = topPos + 'px';
        this.dropdown.style.left = (rect.left + window.scrollX) + 'px';

        users.forEach(user => {
            const item = document.createElement('div');
            item.className = "px-4 py-3 hover:bg-gray-50 dark:hover:bg-zinc-700 cursor-pointer flex items-center gap-3 border-b border-gray-50 dark:border-zinc-700 last:border-0";
            item.innerHTML = `
                ${user.photoURL
                    ? `<img src="${user.photoURL}" class="w-8 h-8 rounded-full object-cover">`
                    : `<div class="w-8 h-8 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center font-bold text-xs">${this.escapeHtml(user.displayName)?.[0] || 'U'}</div>`
                }
        <div class="flex flex-col">
            <span class="text-sm font-bold text-gray-900 dark:text-gray-100">${this.escapeHtml(user.displayName) || 'Unknown'}</span>
            <span class="text-xs text-gray-400">@${user.email?.split('@')[0]}</span>
        </div>
        `;

            // [FIX] Mobile Touch Defense
            // 'touchstart' fires before blur on mobile. Prevent default to keep focus.
            const selectHandler = (e) => {
                e.preventDefault(); // Critical: Prevents input blur
                e.stopPropagation();
                this.selectUser(user, searchTerm, block, inputEl, atIndex);
            };

            item.ontouchstart = selectHandler;
            item.onmousedown = selectHandler;
            this.dropdown.appendChild(item);
        });
    }

    hideDropdown() {
        this.dropdown.classList.add('hidden');
    }

    selectUser(user, searchTerm, block, inputEl, atIndex) {
        const text = inputEl.value;
        // Construct new text: part before '@' + '@Name ' + part after cursor
        // atIndex is the index of '@'

        // Safety: ensure atIndex is still valid
        if (atIndex < 0 || atIndex >= text.length) return;

        const beforeAt = text.substring(0, atIndex);
        // We replace "searchTerm" (content after @) with "displayName "
        const cursor = inputEl.selectionStart;
        const afterCursor = text.substring(cursor);

        const newText = beforeAt + `@${user.displayName} ` + afterCursor;

        inputEl.value = newText;
        block.text = newText; // Sync

        // Move cursor to end of inserted name
        const newCursorPos = atIndex + user.displayName.length + 2; // @ + name + space
        inputEl.setSelectionRange(newCursorPos, newCursorPos);
        inputEl.focus();

        this.hideDropdown();

        block.mentionedUsers.set(user.displayName, user.uid);
    }

    // --- Image Compression ---
    async compressImage(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 1280;
                    let width = img.width;
                    let height = img.height;

                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        resolve(blob || file);
                    }, 'image/webp', 0.8);
                };
                img.onerror = () => resolve(file);
            };
            reader.onerror = () => resolve(file);
        });
    }

    // --- Submit ---
    async handleSubmit() {
        // Validate
        const validBlocks = this.blocks.filter(b => b.text.trim() || b.files.length > 0);
        if (validBlocks.length === 0) return;

        if (!window.user) {
            // [FIX] Use standardized AuthRequireModal
            const { AuthRequireModal } = await import('../../modals/AuthRequireModal.js');
            const { ModalManager } = await import('../../../utils/ModalManager.js'); // Dynamic Import to avoid cycle if any
            ModalManager.show(AuthRequireModal);
            return;
        }

        try {
            this.btnSubmit.disabled = true;
            this.btnSubmit.innerHTML = `<span class="loader-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full"></span> ${LocaleService.t('plaza.btn_submitting')}`;

            // Prepare Data for each block
            const postsData = [];

            for (const block of validBlocks) {
                const imageUrls = [];
                const storagePaths = [];

                if (block.files.length > 0) {
                    const uploadPromises = block.files.map(async (item) => {
                        const compressedBlob = await this.compressImage(item.file);
                        const timestamp = Date.now();
                        const randomId = Math.random().toString(36).substring(2, 8);
                        const filename = `plaza_${window.user.uid}_${timestamp}_${randomId}.webp`;
                        const storageRef = ref(storage, `plaza_images / ${window.user.uid}/${filename}`);
                        const snapshot = await uploadBytes(storageRef, compressedBlob);
                        const url = await getDownloadURL(snapshot.ref);
                        return { url, path: snapshot.ref.fullPath };
                    });
                    const results = await Promise.all(uploadPromises);
                    results.forEach(res => {
                        imageUrls.push(res.url);
                        storagePaths.push(res.path);
                    });
                }

                const hashtags = (block.text.match(/#[\w가-힣\[\]\(\)\-\.]+/g) || []);
                const mentions = [];
                block.mentionedUsers.forEach((uid, name) => {
                    if (block.text.includes(`@${name}`)) mentions.push(uid);
                });

                // [QUOTE LOGIC]
                let type = imageUrls.length > 0 ? 'image' : 'text';
                let quoteData = null;

                // Only the *first* block gets the quote attachment (usually)
                if (this.quoteFeed && postsData.length === 0) {
                    type = 'quote';
                    // Sanitize quote data
                    quoteData = {
                        id: this.quoteFeed.id,
                        authorId: this.quoteFeed.authorId,
                        authorName: this.quoteFeed.authorName,
                        content: this.quoteFeed.content || '',
                        // ... minimal snapshot
                        imageUrls: this.quoteFeed.imageUrls || [],
                        createdAt: this.quoteFeed.createdAt
                    };
                }

                postsData.push({
                    content: block.text.trim(),
                    hashtags,
                    imageUrls,
                    storagePaths,
                    authorId: window.user.uid,
                    authorName: window.user.displayName || '익명의 여행자',
                    authorPhoto: window.user.photoURL || null,
                    authorEmail: window.user.email,
                    isAdminContent: window.user.role === 'admin' || window.user.role === 'super_admin',
                    type: type,
                    quoteData: quoteData, // [NEW]
                    mentions
                });
            }

            // Submit Thread
            await PlazaService.createThread(postsData);

            // [UX] Wait for Server Indexing (Propagation)
            // User Issue: Closing too fast leads to stale "Bubble" icon because `hasThread` or `count` isn't updated on server yet.
            // Increased to 2000ms to ensure the Cloud Function Trigger (Count Increment) also finishes.
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Reset
            this.blocks = [];
            this.blockCounter = 0;
            this.cancelQuoteMode(); // [NEW]
            this.addBlockData();
            this.renderBlocks();

            this.btnSubmit.disabled = false;
            this.btnSubmit.innerHTML = `<span class="material-symbols-outlined">send</span> <span>${LocaleService.t('plaza.btn_submit')}</span>`;

            if (this.onSuccess) this.onSuccess();

        } catch (e) {
            console.error("Post Error:", e);
            alert("업로드 중 오류가 발생했습니다: " + e.message);
            this.btnSubmit.disabled = false;
            this.btnSubmit.innerText = "재시도";
        }
    }
}
